import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { services } from '../data/mock';
import { CheckCircle2, ArrowRight, Phone } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollAnimation';

const ServicePage = () => {
  const location = useLocation();
  const serviceId = location.pathname.replace('/', '');
  const service = services[serviceId];

  const [mainRef, mainVisible] = useScrollReveal({ threshold: 0.1 });
  const [cardsRef, cardsVisible] = useScrollReveal({ threshold: 0.1 });

  if (!service) {
    return (
      <div>
        <PageHeader title="Service Not Found" breadcrumbs={[{ label: 'Services' }]} />
        <div className="py-20 text-center">
          <p className="text-muted-foreground mb-4">The service you're looking for doesn't exist.</p>
          <Link to="/" style={{ color: '#43a047' }} className="text-sm hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <PageHeader
        title={service.title}
        breadcrumbs={[
          { label: 'Product & Services', href: '/product-services' },
          { label: service.title },
        ]}
      />

      <div className="py-14 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* ── Main ── */}
            <div className="flex-1" ref={mainRef}>
              <div className={`${mainVisible ? 'anim-slide-right' : 'reveal-hidden'}`}>
                <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#43a047' }}>Service</p>
                <h2 className="text-3xl font-extrabold text-foreground mb-2">{service.title}</h2>
                <div className="w-14 h-1 rounded-full mb-5" style={{ background: '#43a047' }} />
                <p className="text-muted-foreground leading-relaxed mb-10">{service.description}</p>
              </div>

              <div className={`${mainVisible ? 'anim-fade-up delay-200' : 'reveal-hidden'}`}>
                <h3 className="text-xl font-bold text-foreground mb-6">How We Do It</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" ref={cardsRef}>
                {service.items.map((item, i) => (
                  <div
                    key={i}
                    className={`card-hover border border-border rounded-xl p-6 bg-card group ${cardsVisible ? 'anim-fade-up' : 'reveal-hidden'}`}
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <span
                        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
                        style={{ backgroundColor: '#43a047' }}
                      >
                        {i + 1}
                      </span>
                      <h4 className="font-bold text-foreground pt-0.5">{item.title}</h4>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed pl-10">{item.desc}</p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-1 text-xs font-semibold mt-4 pl-10 group/link"
                      style={{ color: '#43a047' }}
                    >
                      Get Started
                      <ArrowRight size={12} className="group-hover/link:translate-x-1" style={{ transition: 'transform 0.2s' }} />
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Sidebar ── */}
            <div className="lg:w-72 space-y-5">
              {/* Services List */}
              <div className="bg-muted border border-border rounded-xl p-5">
                <h4 className="font-bold text-foreground mb-4 text-xs uppercase tracking-wider">All Services</h4>
                <ul className="space-y-1">
                  {Object.entries(services).map(([key, s]) => (
                    <li key={key}>
                      <Link
                        to={`/${key}`}
                        className={`flex items-center gap-2 text-sm py-2 px-3 rounded-lg ${
                          key === serviceId
                            ? 'text-white font-semibold'
                            : 'text-muted-foreground hover:text-[#43a047] hover:bg-muted'
                        }`}
                        style={{
                          backgroundColor: key === serviceId ? '#43a047' : 'transparent',
                          transition: 'all 0.2s',
                        }}
                      >
                        <ArrowRight size={12} />
                        {s.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA box */}
              <div className="rounded-xl p-5 text-white relative overflow-hidden" style={{ backgroundColor: '#43a047' }}>
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10" />
                <h4 className="font-bold mb-2 text-sm relative">Need Help?</h4>
                <p className="text-xs text-green-100 leading-relaxed mb-4 relative">
                  Contact our experts to find the right solution for your business.
                </p>
                <a
                  href="tel:941-799-8809"
                  className="relative flex items-center gap-2 text-sm font-semibold bg-white text-[#43a047] px-4 py-2 rounded-full"
                  style={{ transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e8f5e9'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                >
                  <Phone size={14} />
                  941-799-8809
                </a>
              </div>

              {/* Why Choose */}
              <div className="bg-muted border border-border rounded-xl p-5">
                <h4 className="font-bold text-foreground mb-4 text-xs uppercase tracking-wider">Why Choose Us</h4>
                <ul className="space-y-2">
                  {[
                    '32 Million+ B2B Contacts',
                    '40+ Industry Segments',
                    'Dedicated Account Manager',
                    'Real-Time Lead Delivery',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 size={14} style={{ color: '#43a047' }} className="flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicePage;
